import React, { useEffect, useState } from "react";
import HeartLoader from "@/components/HeartLoader";
import { useParams, Link } from "react-router-dom";
import { Loader2, MapPin, CalendarClock, Users, ArrowLeft, Share2, Quote, ExternalLink, Ticket, PartyPopper } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const imgSrc = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : "");
const money = (sym, amt) => `${sym || ""}${Number(amt || 0).toLocaleString()}`;

export default function PublicEvent() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/events/${id}`).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [id]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: data?.listing?.title, url }); return; } catch (_) {} }
    try { await navigator.clipboard.writeText(url); toast.success("Invite link copied!"); } catch { toast.error("Couldn't copy the link."); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center"><HeartLoader /></div>;
  if (!data) return <div className="min-h-screen grid place-items-center px-6 text-center"><div><h1 className="font-serif text-2xl">Event not found</h1><Link to="/events" className="text-brand hover:underline text-sm">Browse events</Link></div></div>;

  const e = data.listing;
  const isMeal = e.kind === "meal";
  const boardPath = isMeal ? "/meals" : "/events";
  const hasCoords = typeof e.lat === "number" && typeof e.lng === "number";
  const d = 0.008;
  const bbox = hasCoords ? `${e.lng - d},${e.lat - d},${e.lng + d},${e.lat + d}` : "";
  const mapEmbed = hasCoords ? `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${e.lat},${e.lng}` : "";
  const mapsLink = hasCoords ? `https://www.google.com/maps?q=${e.lat},${e.lng}` : (e.address ? `https://www.google.com/maps/search/${encodeURIComponent(e.address)}` : "");

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-24 pb-16" data-testid="public-event-page">
      <Link to={boardPath} className="text-xs text-sec hover:text-brand flex items-center gap-1 mb-4"><ArrowLeft size={13} /> Back to community {isMeal ? "meals" : "events"}</Link>

      <div className="rud-card rounded-2xl overflow-hidden">
        {e.cover && <div className="h-56 w-full overflow-hidden"><img src={imgSrc(e.cover)} alt={e.title} className="w-full h-full object-cover" data-testid="pe-cover" /></div>}
        <div className="p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="text-[10px] uppercase tracking-widest text-brand flex items-center gap-1"><PartyPopper size={12} /> {e.type}</span>
              <h1 className="font-serif text-3xl mt-1 leading-tight" data-testid="pe-title">{e.title}</h1>
            </div>
            <button data-testid="pe-share" onClick={share} className="btn-ghost rounded-full h-9 px-3 flex items-center gap-1.5 text-xs text-brand shrink-0"><Share2 size={14} /> Share invite</button>
          </div>

          <div className="text-xs text-sec mt-2 flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1"><MapPin size={12} /> {e.address || "Location shared on booking"}</span>
            {e.when && <span className="flex items-center gap-1"><CalendarClock size={12} /> {e.when}</span>}
            <span className="flex items-center gap-1"><Users size={12} /> {e.seats_left} of {e.capacity} seats left</span>
            <span>by {e.host_name}{e.host_reviews ? ` · ${e.host_rating}★ (${e.host_reviews})` : ""}</span>
          </div>

          {e.status === "cancelled" && <div className="mt-3 text-sm text-red-400">This {isMeal ? "meal" : "event"} was cancelled by the host.</div>}

          {e.description && <p className="text-sm mt-4">{e.description}</p>}
          {!isMeal && e.artists && <p className="text-sm mt-3"><b className="text-brand">Line-up:</b> {e.artists}</p>}
          {!isMeal && e.activities && <p className="text-sm mt-2"><b className="text-brand">Activities:</b> {e.activities}</p>}
          {isMeal && e.menu && <p className="text-sm mt-3"><b className="text-brand">Menu:</b> {e.menu}</p>}
          {e.whats_included && <p className="text-xs text-sec mt-2"><b>Included:</b> {e.whats_included}</p>}

          {(e.attire || e.age_group) && (
            <div className="flex flex-wrap gap-2 mt-3 text-xs">
              {e.attire && <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1">👗 {e.attire}</span>}
              {e.age_group && <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1">👪 {e.age_group}</span>}
              {e.couples_only && <span className="rounded-full bg-brand/15 text-brand px-3 py-1">💑 Couples only</span>}
              {e.kids_welcome && <span className="rounded-full bg-brand/15 text-brand px-3 py-1">🧒 Kids welcome</span>}
            </div>
          )}

          {/* Price */}
          <div className="mt-4 rounded-xl bg-brand/5 border border-brand/20 p-4 flex items-center justify-between">
            <div>
              <div className="text-2xl font-serif text-brand" data-testid="pe-price">{e.is_free ? "Free" : `${money(e.symbol, e.price_per_person)} / person`}</div>
              {!e.is_free && <div className="text-[11px] text-sec">Reserve with 15% ({money(e.symbol, e.platform_fee)}) via UPI · host gets 85% ({money(e.symbol, e.host_amount)})</div>}
            </div>
          </div>

          {/* Map */}
          {(hasCoords || e.address) && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-widest text-sec mb-2 flex items-center gap-1"><MapPin size={12} /> Where</div>
              {hasCoords
                ? <iframe title="map" data-testid="pe-map" src={mapEmbed} className="w-full h-56 rounded-xl border border-white/10" loading="lazy" />
                : <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-sm">{e.address}</div>}
              {mapsLink && <a href={mapsLink} target="_blank" rel="noopener noreferrer" className="text-xs text-brand hover:underline mt-2 inline-flex items-center gap-1" data-testid="pe-maps-link">Open in Google Maps <ExternalLink size={11} /></a>}
            </div>
          )}

          {/* Reviews */}
          {(() => { const revs = (data.reviews && data.reviews.length) ? data.reviews : (e.top_reviews || []); return revs.length > 0 && (
            <div className="mt-5" data-testid="pe-reviews">
              <div className="text-xs uppercase tracking-widest text-sec mb-2">What guests say</div>
              <div className="space-y-2">
                {revs.slice(0, 4).map((r, i) => (
                  <div key={r.id || i} className="rud-card rounded-xl p-3 flex items-start gap-2">
                    <Quote size={13} className="text-brand shrink-0 mt-0.5" />
                    <p className="text-sm text-sec italic">{r.comment || "Loved it!"} <span className="not-italic text-[var(--rud-text)]">— {r.reviewer_name} · {r.rating}★</span></p>
                  </div>
                ))}
              </div>
            </div>
          ); })()}

          {/* CTA */}
          {e.status === "open" && e.seats_left > 0 && (
            <Link to={boardPath} data-testid="pe-reserve-cta" className="btn-primary w-full h-12 rounded-full mt-6 flex items-center justify-center gap-2 text-sm">
              <Ticket size={16} /> Reserve on My Kind
            </Link>
          )}
          <p className="text-[11px] text-sec text-center mt-3">One family · Vasudhaiva Kutumbakam 🌿</p>
        </div>
      </div>
    </div>
  );
}
